import type { Population, ViewerQuery } from "src/graphql/generated";

/** Viewer is the resolved caller, as the backend reports them. */
export type Viewer = ViewerQuery["viewer"];

/**
 * canWrite reports whether the caller may act on a given population.
 *
 * The console hides what a caller cannot do, but this is presentation only — the
 * backend refuses it regardless, and that is the actual guarantee. A hidden button
 * is a courtesy, not a permission check.
 */
export function canWrite(viewer: Viewer, population: string): boolean {
  return viewer.writablePopulations.includes(population as Viewer["writablePopulations"][number]);
}

/**
 * canRead is the same question for reading, and carries the same caveat.
 *
 * Used where a field is served to some readers and not others, so the console can
 * leave the column out rather than render it empty — a blank cell would say "nobody
 * recorded one", which is a different claim from "not yours to see" and the wrong one.
 * The backend answers the narrower question itself; every writer role includes read in
 * its own population, so this is never the stricter of the two.
 */
export function canRead(viewer: Viewer, population: string): boolean {
  return viewer.readablePopulations.includes(population as Viewer["readablePopulations"][number]);
}

/**
 * How the People list should offer its Population filter.
 *
 * A chooser is worth rendering only where there is something to choose between, so
 * the three cases are settled here rather than by length checks spread through the
 * JSX — and the caller who reads nothing then cannot fall through to a rendering
 * that names a population.
 *
 * That third case is defensive, not reachable, and the reason given for it here was
 * wrong. It said the case was "real rather than defensive" because an admin_user row
 * and its grants are separate records and the People tab is shown to everyone. The
 * row without grants is indeed a real database state — but such a caller never gets
 * a viewer to render from: `Authenticator.Middleware` answers 401 for
 * `len(actor.Roles) == 0` before GraphQL runs, `rolesOf` fails closed on a role the
 * code does not know, the dev bypass resolves an iam-admin, and all five roles grant
 * read somewhere. So `readablePopulations` is never empty here.
 * `TestMiddlewareRejectsUnknownDisabledAndRolelessAccounts` pins that, and is what
 * would fail if the boundary were relaxed — at which point this branch becomes live
 * and INFRA-668 records what it should then say.
 *
 * What such a person actually gets is `notProvisioned`: `handleCallback` refuses them
 * a session and serves a 403 saying they have no access and to ask an administrator.
 * A session revoked while in use 401s to the login screen, and signing in again lands
 * on that same 403.
 *
 * Kept anyway, because it costs one expression and the wrong answer is worse than a
 * crash: humanPopulation returns "Partner" for anything that is not an on-demand
 * contractor, `undefined` included.
 */
export type PopulationFilterChoice =
  | { kind: "choose"; populations: readonly Population[] }
  | { kind: "fixed"; population: Population }
  | { kind: "none" };

export function populationFilterChoice(viewer: Viewer): PopulationFilterChoice {
  const populations = viewer.readablePopulations;
  if (populations.length > 1) {
    return { kind: "choose", populations };
  }
  // Taken by index rather than guarded by a length check, so the empty case cannot
  // reach `fixed`: humanPopulation answers "Partner" for anything that is not an
  // on-demand contractor, `undefined` included, and would name a population to
  // somebody entitled to read neither.
  const only = populations[0];
  return only ? { kind: "fixed", population: only } : { kind: "none" };
}
